import React, { useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';

import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';
import { checkIsEqual } from '@confluence/table-of-contents/entry-points/checkIsEqual.ts';
import { TableOfContentsLoader } from '@confluence/table-of-contents/entry-points/TableOfContentsLoader.ts';
import type { SubscriptionHandlerFnType } from '@confluence/editor-features/entry-points/SubscriptionHandlerFnType.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { OuterEditorViewContext } from './OuterEditorViewContext.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `${token('space.250')} ${token('space.250')}`,
	backgroundColor: token('color.background.neutral.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Centered = styled.div({
	display: 'flex',
	justifyContent: 'center',
});

type TableOfContentsLiveComponentProps = {
	macroDefaultProps: any;
	editorActions: EditorActions;
	subscribeDocChange: SubscriptionHandlerFnType;
	unsubscribeDocChange: SubscriptionHandlerFnType;
	// TODO: (AFC-319) To remove the need for this prop and find a more elegant for collabEdit support of smart creates
	isCollabEditForLiveUpdatesEnabled: boolean;
};

export const TableOfContentsLiveComponent = ({
	macroDefaultProps,
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isCollabEditForLiveUpdatesEnabled,
}: TableOfContentsLiveComponentProps): React.JSX.Element => {
	const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
	const [adf, setAdf] = useState<ADNode | undefined>();
	const { userId, cloudId } = useSessionData();
	const debounceMs = 400;
	const previousRemoteChangeTimeRef = useRef<number | null | undefined>(undefined);
	const previousLocalChangeTimeRef = useRef<number | null | undefined>(undefined);
	const previousADF = useRef<ADNode | undefined>();
	// Provided by the LCM extension-handler when this ToC is rendered inside a Legacy Content Macro.
	// When inside an LCM, editorActions is scoped to the LCM's nested editor and therefore only shows headings within the LCM.
	// Use the outerEditorView to show all page headings in this case.
	// outerEditorView be undefined when rendered outside an LCM so we'll use normal editorActions in this case.
	const outerEditorView = useContext(OuterEditorViewContext);

	const getEffectiveEditorView = useCallback(() => {
		return outerEditorView ?? editorActions._privateGetEditorView();
	}, [outerEditorView, editorActions]);

	const updateAdf = useCallback(
		(editorView: EditorView | undefined) => {
			const effectiveView = outerEditorView ?? editorView;
			const doc = effectiveView?.state?.doc;

			if (doc) {
				const editorTransformer = new JSONTransformer();
				const _adf = editorTransformer.encode(doc);

				if (isCollabEditForLiveUpdatesEnabled) {
					const shouldUpdateADF = previousADF.current
						? !checkIsEqual(previousADF.current, _adf, {
								includeIncludedContentMacros: expValEquals(
									'confluence_toc_included_content_exp',
									'isEnabled',
									true,
								),
							})
						: true;
					previousADF.current = _adf as ADNode;
					if (shouldUpdateADF) {
						setAdf(_adf as ADNode);
					}
				} else {
					setAdf(_adf as ADNode);
				}
			}
		},
		[isCollabEditForLiveUpdatesEnabled, outerEditorView],
	);

	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	const debouncedUpdateADF = useMemo(
		() =>
			debounce(() => {
				updateAdf(getEffectiveEditorView());
			}, debounceMs),
		[getEffectiveEditorView, updateAdf],
	);

	usePluginStateEffect(fullPageEditorPresetAPI, ['collabEdit'], ({ collabEditState }) => {
		const lastRemoteChangeTime = collabEditState?.initialised?.lastRemoteOrganicBodyChangeAt;
		const lastLocalChangeTime = collabEditState?.initialised?.lastLocalOrganicBodyChangeAt;
		const hasRemoteChanged =
			lastRemoteChangeTime && lastRemoteChangeTime !== previousRemoteChangeTimeRef.current;
		const hasLocalChanged =
			lastLocalChangeTime && lastLocalChangeTime !== previousLocalChangeTimeRef.current;
		if (hasRemoteChanged || hasLocalChanged) {
			previousRemoteChangeTimeRef.current = lastRemoteChangeTime;
			previousLocalChangeTimeRef.current = lastLocalChangeTime;
			if (isCollabEditForLiveUpdatesEnabled) {
				debouncedUpdateADF();
			}
		}
	});

	useEffect(() => {
		const debouncedHandleDocChange = debounce(updateAdf, debounceMs);
		if (!isCollabEditForLiveUpdatesEnabled) {
			subscribeDocChange(debouncedHandleDocChange);
		}

		return () => {
			if (!isCollabEditForLiveUpdatesEnabled) {
				unsubscribeDocChange(debouncedHandleDocChange);
			}
		};
	}, [
		subscribeDocChange,
		unsubscribeDocChange,
		debounceMs,
		updateAdf,
		isCollabEditForLiveUpdatesEnabled,
	]);

	useEffect(() => {
		// Ideally we would rely on the doc change handler to populate the ADF
		// but the doc change handler is sometimes not called on initial load.
		updateAdf(getEffectiveEditorView());
		setIsInitialLoadComplete(true);
	}, [getEffectiveEditorView, updateAdf]);

	if (!isInitialLoadComplete) {
		return (
			<Centered>
				<Spinner interactionName="table-of-contents-live-component" />
			</Centered>
		);
	} else if (!adf) {
		return <div />;
	}

	return (
		<Container>
			<TableOfContentsLoader
				isInEditMode
				userId={userId ?? ''}
				cloudId={cloudId ?? ''}
				adf={adf}
				macroDefaultProps={macroDefaultProps}
			/>
		</Container>
	);
};
